import type { ReactNode } from "react";
import "./ui.css";

interface PageHeaderProps {
	title: string;
	sublabel?: string;
	/**
	 * EQ-2278 item 2. "eyebrow" is the 11px uppercase default every existing call
	 * site renders; "header" makes the sublabel legible as a page header while
	 * staying subordinate to the title. Optional and defaulted, so
	 * StaffAccessPage and Financial are byte-identical.
	 */
	sublabelSize?: "eyebrow" | "header";
	/**
	 * EQU-796. Sits inline after the title, for a scope marker the title itself
	 * cannot carry — AutoRec shows which company's books are open. Keep it to a
	 * single Chip; it is not an actions slot.
	 */
	titleAdornment?: ReactNode;
	/**
	 * EQU-803. A sentence under the title that says what the page holds. Distinct
	 * from `sublabel`, which renders ABOVE the title as a parent/section label —
	 * a subtitle reads as the title's own explanation, so it has to follow it.
	 */
	subtitle?: ReactNode;
	/**
	 * "lg" (28px/600) is the landing-page scale. Default stays the 20px/700 every
	 * existing call site renders, so none of them move.
	 */
	size?: "md" | "lg";
	actions?: ReactNode;
}

/* Light page header (.firm-toolbar). Renders NO dark/forest band — a dark
   header on a functional page reads as an error. */
export function PageHeader({
	title,
	sublabel,
	sublabelSize = "eyebrow",
	titleAdornment,
	subtitle,
	size = "md",
	actions,
}: PageHeaderProps) {
	const titleClassName = `ui-page-header-title${size === "lg" ? " is-lg" : ""}`;
	return (
		<div className="ui-page-header">
			<div className="ui-page-header-titles">
				{sublabel && (
					<p
						className={`ui-page-header-sublabel${
							sublabelSize === "header" ? " is-header" : ""
						}`}
					>
						{sublabel}
					</p>
				)}
				{titleAdornment ? (
					<div className="ui-page-header-title-row">
						<h1 className={titleClassName}>{title}</h1>
						{titleAdornment}
					</div>
				) : (
					<h1 className={titleClassName}>{title}</h1>
				)}
				{/* div, not p: typed ReactNode, and a caller passing a block element
				    inside a <p> produces a validateDOMNesting warning and a mangled
				    layout — the same trap StatNumber documents for its own sub. */}
				{subtitle != null && (
					<div className="ui-page-header-subtitle">{subtitle}</div>
				)}
			</div>
			{actions && <div className="ui-page-header-actions">{actions}</div>}
		</div>
	);
}
